import type { Locale } from "../api/types";
import { chromeLocale } from "../lib/locale-context";

/**
 * Emergency OTP bypass (batch 17, design EMERGENCY-OTP-DESIGN-20261010 §6): the card's ⋯ entry, the request and code dialogs,
 * the red banner, bypassed moves, the approver's code page, the bell phrases and Settings → Security. English shows for every
 * user (`chromeLocale`, ruling 2026-10-04); the Thai is provisional, carried for a later decision, and unconfirmed by a Thai
 * reviewer. Crew words follow the product: PM = Capt., PD = Chief Pilot.
 */
export type EmergencyTextKey =
  | "menuRequest"
  | "menuEnter"
  | "menuEnd"
  | "requestTitle"
  | "requestBody"
  | "requestWho"
  | "requestWhoBody"
  | "requestPdpa"
  | "reasonLabel"
  | "reasonRequired"
  | "reasonCount"
  | "reasonShort"
  | "reasonLong"
  | "send"
  | "sending"
  | "cancel"
  | "close"
  | "rateLimited"
  | "rateLimitedNoTime"
  | "noApprover"
  | "notConfigured"
  | "alreadyActive"
  | "staffOnly"
  | "testMode"
  | "codeTitle"
  | "sentProjectDirector"
  | "sentFallback"
  | "sentSelf"
  | "channelBoth"
  | "channelInApp"
  | "callThem"
  | "codeLabel"
  | "codeLabelOther"
  | "codeFormat"
  | "expiresIn"
  | "expiresSoon"
  | "attemptsLeft"
  | "attemptLeft"
  | "attemptsLeftShort"
  | "attemptLeftShort"
  | "unlock"
  | "unlocking"
  | "cancelRequest"
  | "cancelling"
  | "newRequest"
  | "newRequestNote"
  | "expired"
  | "locked"
  | "notPending"
  | "grantedAnnounce"
  | "cancelledAnnounce"
  | "bannerTitle"
  | "bannerBody"
  | "bannerBodyYou"
  | "bannerFrom"
  | "selfIssued"
  | "viewMoves"
  | "endTitle"
  | "endBodyHolder"
  | "endBodyOther"
  | "endNote"
  | "endNoteOptional"
  | "endNoteRequired"
  | "endNoteShort"
  | "endConfirm"
  | "ending"
  | "endedAnnounce"
  | "notActive"
  | "moveChip"
  | "moveSkips"
  | "moveSkipsNone"
  | "moveConfirm"
  | "moveOptional"
  | "pageTitle"
  | "pageLoading"
  | "pageNotAvailable"
  | "pageCard"
  | "pageRequester"
  | "pageReason"
  | "pageStatus"
  | "pageExpiresIn"
  | "showCode"
  | "showingCode"
  | "codeAria"
  | "copy"
  | "copied"
  | "copyFailed"
  | "giveOnly"
  | "revealGone"
  | "outcomeConsumed"
  | "outcomeExpired"
  | "outcomeLocked"
  | "outcomeSuperseded"
  | "outcomeCancelled"
  | "outcomePending"
  | "bellRequested"
  | "bellExpiresIn"
  | "bellExpired"
  | "bellGranted"
  | "bellMoved"
  | "bellLocked"
  | "bellEnded"
  | "bellStillActive"
  | "attention"
  | "fallbackHeading"
  | "fallbackLabel"
  | "fallbackHelp"
  | "fallbackEveryone"
  | "fallbackReason"
  | "fallbackSave"
  | "fallbackSaving"
  | "fallbackSaved"
  | "fallbackNoAdmins"
  | "fallbackStale"
  | "historyVia"
  | "historyField"
  | "youSuffix"
  | "reportTitle"
  | "reportSub"
  | "reportNotAllowed"
  | "reportFlagOff"
  | "reportTabs"
  | "reportTabBypasses"
  | "reportTabRequests"
  | "reportFilters"
  | "reportProject"
  | "reportAllProjects"
  | "reportFrom"
  | "reportTo"
  | "reportRequestedFrom"
  | "reportShow"
  | "reportActive"
  | "reportEnded"
  | "reportAll"
  | "reportAllRequests"
  | "reportRequester"
  | "reportAnyone"
  | "reportClear"
  | "reportColCard"
  | "reportColProject"
  | "reportColRequester"
  | "reportColCodeFrom"
  | "reportColReason"
  | "reportColGranted"
  | "reportColEnded"
  | "reportColEndedBecause"
  | "reportColDuration"
  | "reportColMoves"
  | "reportColApprovers"
  | "reportColRequested"
  | "reportColStatus"
  | "reportColAttempts"
  | "reportColClosed"
  | "reportActiveFor"
  | "reportStillActive"
  | "reportEndedDone"
  | "reportEndedHolder"
  | "reportEndedRevoked"
  | "reportEndedIneligible"
  | "reportEndedBy"
  | "reportReqPending"
  | "reportReqExpired"
  | "reportReqLocked"
  | "reportReqSuperseded"
  | "reportReqCancelled"
  | "reportReqUsed"
  | "reportBasisPd"
  | "reportBasisFallback"
  | "reportShowMoves"
  | "reportHideMoves"
  | "reportMovesOf"
  | "reportNoMoves"
  | "reportNothingSkipped"
  | "reportSkips"
  | "reportLoading"
  | "reportLoadFailed"
  | "reportEmpty"
  | "reportEmptyRequests"
  | "reportCountBypass"
  | "reportCountBypasses"
  | "reportCountRequest"
  | "reportCountRequests"
  | "reportMoreAvailable"
  | "reportAutomated"
  | "reportLoadMore"
  | "reportLoadingMore"
  | "reportExport"
  | "reportExporting"
  | "reportExported"
  | "reportExportCapped"
  | "reportExportFailed"
  | "reportDateOrder"
  | "reportLockedNote"
  | "requestWhoLoading"
  | "requestWhoNamed"
  | "requestWhoPerson"
  | "requestWhoAnd"
  | "whoChannelBoth"
  | "whoChannelBell"
  | "requestWhoSelf"
  | "boardChip"
  | "reportAnyDate"
  | "reportExportBypasses"
  | "reportExportRequests"
  | "reportExportNothing"
  | "reportExportFixDates";

export const EMERGENCY_TEXT: Record<Locale, Record<EmergencyTextKey, string>> = {
  en: {
    menuRequest: "Request Emergency OTP",
    menuEnter: "Enter emergency code",
    menuEnd: "End emergency bypass",
    requestTitle: "Request an emergency code",
    requestBody:
      "Use this only in an emergency. The code goes to the Chief Pilot of {project}. With it you can make any move on {key}, skipping rules, approvals and roles, until the card is done. Every move is recorded and reported to the Chief Pilot.",
    requestWho: "Who gets the code",
    requestWhoBody:
      "The Chief Pilot of {project}: by Telegram when they have linked it, and always in Giraffe. With no Chief Pilot, the emergency fallback approver gets it. You see their name as soon as you send.",
    requestPdpa: "Your name and this reason are sent to them, also by Telegram.",
    reasonLabel: "Reason",
    reasonRequired: "(required)",
    reasonCount: "10 to 500 characters · {n} so far",
    reasonShort: "Give a reason of at least 10 characters ({n} so far).",
    reasonLong: "Keep the reason to 500 characters ({n} now).",
    send: "Send request",
    sending: "Sending…",
    cancel: "Cancel",
    close: "Close",
    rateLimited: "You can request again at {time}.",
    rateLimitedNoTime: "Too many emergency requests on this card. Try again later.",
    noApprover: "Nobody can receive an emergency code for this project. Ask a System Admin to set the emergency fallback approver in Settings → Security.",
    notConfigured: "Emergency codes are not set up on this server.",
    alreadyActive: "You already hold an emergency bypass on this card.",
    staffOnly: "Emergency codes are for TFG staff only.",
    testMode: "Emergency codes cannot be used in test mode. Turn test mode off first.",
    codeTitle: "Enter the emergency code for {key}",
    sentProjectDirector: "Sent to {names}, Chief Pilot of {project}, {channel}.",
    sentFallback: "Sent to the emergency fallback approver, {names}, {channel}.",
    sentSelf: "Self-issued: you are the last approver for this project. This will be reported.",
    channelBoth: "by Telegram and in Giraffe",
    channelInApp: "in Giraffe only (Telegram is not linked)",
    callThem: "Call them or ask in person for the code.",
    codeLabel: "6-digit code from the Chief Pilot",
    codeLabelOther: "6-digit code from the approver",
    codeFormat: "Enter the 6 digits of the code.",
    expiresIn: "Expires in {time}",
    expiresSoon: "The code expires in 1 minute.",
    attemptsLeft: "Wrong code. {n} attempts left.",
    attemptLeft: "Wrong code. 1 attempt left.",
    attemptsLeftShort: "{n} attempts left",
    attemptLeftShort: "1 attempt left",
    unlock: "Unlock",
    unlocking: "Checking…",
    cancelRequest: "Cancel request",
    cancelling: "Cancelling…",
    newRequest: "Request a new code",
    newRequestNote: "A new request replaces this one: its code stops working.",
    expired: "This code has expired. Request a new one if you still need it.",
    locked: "Locked after 5 wrong codes. The approver was told. Request a new code if you still need one.",
    notPending: "This request is no longer open. Request a new code if you still need one.",
    grantedAnnounce: "Emergency bypass active on {key}",
    cancelledAnnounce: "Emergency code request cancelled",
    bannerTitle: "Emergency bypass active",
    bannerBody: "{holder} can make any move on this card until it is done.",
    bannerBodyYou: "You can make any move on this card until it is done.",
    bannerFrom: "Code from {approver} {ago}.",
    selfIssued: "Self-issued",
    viewMoves: "View moves",
    endTitle: "End the emergency bypass on {key}?",
    endBodyHolder: "You can no longer skip rules on this card. A new bypass needs a new code.",
    endBodyOther: "{holder} can no longer skip rules on this card. They and the approvers are told.",
    endNote: "Note",
    endNoteOptional: "(optional)",
    endNoteRequired: "(required)",
    endNoteShort: "Say why, in at least 10 characters ({n} so far).",
    endConfirm: "End bypass",
    ending: "Ending…",
    endedAnnounce: "Emergency bypass ended on {key}",
    notActive: "This bypass has already ended.",
    moveChip: "Emergency",
    moveSkips: "This move skips:",
    moveSkipsNone: "Only the emergency bypass allows this move.",
    moveConfirm: "{label} (emergency)",
    moveOptional: "Fields are optional under the bypass, except those the record needs.",
    pageTitle: "Emergency code",
    pageLoading: "Loading…",
    pageNotAvailable: "This emergency code request is not available to you.",
    pageCard: "Card",
    pageRequester: "Requested by",
    pageReason: "Reason",
    pageStatus: "Status",
    pageExpiresIn: "Expires in {time}",
    showCode: "Show code",
    showingCode: "Loading code…",
    codeAria: "Emergency code {digits}",
    copy: "Copy",
    copied: "Copied",
    copyFailed: "Copy did not work. Read the digits out instead.",
    giveOnly: "Give this code only to {requester}, by phone or in person.",
    revealGone: "The code can no longer be shown: the request was used, has expired or is locked.",
    outcomeConsumed: "Used by {requester} at {time}.",
    outcomeExpired: "Expired.",
    outcomeLocked: "Locked after 5 wrong codes.",
    outcomeSuperseded: "Replaced by a newer request from {requester}.",
    outcomeCancelled: "Cancelled by {requester}.",
    outcomePending: "Waiting for {requester} to enter the code.",
    bellRequested: "requested an emergency code",
    bellExpiresIn: "expires in {n} min",
    bellExpired: "expired",
    bellGranted: "unlocked an emergency bypass",
    bellMoved: "moved this under emergency bypass",
    bellLocked: "Emergency code locked after 5 wrong attempts",
    bellEnded: "Emergency bypass ended",
    bellStillActive: "Emergency bypass still active",
    attention: "Emergency codes waiting ({n})",
    fallbackHeading: "Emergency codes",
    fallbackLabel: "Emergency fallback approver",
    fallbackHelp: "Gets emergency codes for projects with no Chief Pilot. Empty = every System Admin.",
    fallbackEveryone: "Every System Admin (empty)",
    fallbackReason: "Reason",
    fallbackSave: "Save fallback approver",
    fallbackSaving: "Saving…",
    fallbackSaved: "Emergency fallback approver saved.",
    fallbackNoAdmins: "No active System Admin can be chosen.",
    fallbackStale: "Someone changed these settings since you opened the page. Reload to see them.",
    historyVia: "Emergency bypass",
    historyField: "Emergency bypass skipped",
    youSuffix: "(you)",
    reportTitle: "Emergency bypasses",
    reportSub: "Every emergency bypass, who asked, who gave the code, and every move made with it. Chief Pilots see their own projects; System Admins see every project.",
    reportNotAllowed: "The emergency bypasses report is for Chief Pilots and System Admins.",
    reportFlagOff: "Emergency codes are not turned on.",
    reportTabs: "Report sections",
    reportTabBypasses: "Bypasses",
    reportTabRequests: "Requests not used",
    reportFilters: "Filter emergency bypasses",
    reportProject: "Project",
    reportAllProjects: "All my projects",
    reportFrom: "Granted from",
    reportTo: "To",
    reportRequestedFrom: "Requested from",
    reportShow: "Show",
    reportActive: "Active",
    reportEnded: "Ended",
    reportAll: "All",
    reportAllRequests: "All not used",
    reportRequester: "Requester",
    reportAnyone: "Anyone",
    reportClear: "Clear filters",
    reportColCard: "Card",
    reportColProject: "Project",
    reportColRequester: "Requester",
    reportColCodeFrom: "Code from",
    reportColReason: "Reason",
    reportColGranted: "Granted",
    reportColEnded: "Ended",
    reportColEndedBecause: "Ended because",
    reportColDuration: "Duration",
    reportColMoves: "Moves",
    reportColApprovers: "Sent to",
    reportColRequested: "Requested",
    reportColStatus: "Outcome",
    reportColAttempts: "Wrong codes",
    reportColClosed: "Closed",
    reportActiveFor: "active for {time}",
    reportStillActive: "Still active",
    reportEndedDone: "Card done",
    reportEndedHolder: "Ended by the holder",
    reportEndedRevoked: "Ended early",
    reportEndedIneligible: "Holder no longer eligible",
    reportEndedBy: "by {name}",
    reportReqPending: "Waiting for the code",
    reportReqExpired: "Expired",
    reportReqLocked: "Locked after 5 wrong codes",
    reportReqSuperseded: "Replaced by a newer request",
    reportReqCancelled: "Cancelled",
    reportReqUsed: "Used",
    reportBasisPd: "Chief Pilot",
    reportBasisFallback: "Fallback approver",
    reportShowMoves: "Show moves",
    reportHideMoves: "Hide moves",
    reportMovesOf: "Moves under bypass {id} on {key}",
    reportNoMoves: "No moves yet",
    reportNothingSkipped: "Nothing skipped",
    reportSkips: "Skipped: {list}",
    reportLoading: "Loading emergency bypasses…",
    reportLoadFailed: "The emergency bypasses could not be loaded.",
    reportEmpty: "No emergency bypasses match these filters.",
    reportEmptyRequests: "No unused requests match these filters.",
    // D-B18-08 (UX §11.2 rule 5): the count line names what it counts, "{n} bypasses" / "{n} requests", one or many.
    reportCountBypass: "1 bypass",
    reportCountBypasses: "{n} bypasses",
    reportCountRequest: "1 request",
    reportCountRequests: "{n} requests",
    reportMoreAvailable: "{count} · more available",
    // Item 9 (B18 DEV round OBS-B18-8): the report refuses an automated browser; say so rather than naming the roles.
    reportAutomated: "The emergency bypasses report cannot be opened through an AI assistant or an automated browser. Open it in your own browser.",
    reportLoadMore: "Load more",
    reportLoadingMore: "Loading…",
    reportExport: "Export CSV",
    reportExporting: "Exporting…",
    reportExported: "CSV downloaded: {n} rows.",
    reportExportCapped: "Only the first {n} bypasses were exported. Narrow the filters to export the rest.",
    reportExportFailed: "The export failed. Nothing was downloaded.",
    reportDateOrder: "The start date is after the end date.",
    reportLockedNote: "Five wrong codes: check with the requester that it was them.",
    // Batch 18 (B18-UX-SPECS-20261011 §11.2, §11.5, §11.6).
    requestWhoLoading: "Checking who approves…",
    requestWhoNamed: "The code goes to {list}. Call them after you send.",
    requestWhoPerson: "{name} ({role}) {channel}",
    requestWhoAnd: " and ",
    whoChannelBoth: "by Telegram and the bell",
    whoChannelBell: "by the bell only (Telegram is not linked)",
    requestWhoSelf: "You are the approver for this project: the code comes to you.",
    boardChip: "Emergency",
    reportAnyDate: "{label} (any date)",
    reportExportBypasses: "Export bypasses (CSV)",
    reportExportRequests: "Export requests (CSV)",
    reportExportNothing: "Nothing to export with these filters.",
    reportExportFixDates: "Fix the dates to export.",
  },
  th: {
    menuRequest: "ขอรหัส OTP ฉุกเฉิน",
    menuEnter: "ใส่รหัสฉุกเฉิน",
    menuEnd: "ยุติสิทธิ์ฉุกเฉิน",
    requestTitle: "ขอรหัสฉุกเฉิน",
    requestBody:
      "ใช้เฉพาะกรณีฉุกเฉินเท่านั้น รหัสจะส่งถึง Chief Pilot ของ {project} เมื่อได้รหัสแล้ว คุณย้ายสถานะ {key} ได้ทุกขั้นโดยข้ามกฎ การอนุมัติ และบทบาท จนกว่างานจะเสร็จ ทุกการย้ายจะถูกบันทึกและแจ้ง Chief Pilot",
    requestWho: "ใครได้รับรหัส",
    requestWhoBody: "Chief Pilot ของ {project}: ทาง Telegram หากเชื่อมไว้ และใน Giraffe เสมอ หากไม่มี Chief Pilot ผู้อนุมัติสำรองจะได้รับรหัส คุณจะเห็นชื่อทันทีที่ส่ง",
    requestPdpa: "ชื่อของคุณและเหตุผลนี้จะส่งถึงผู้รับ รวมถึงทาง Telegram",
    reasonLabel: "เหตุผล",
    reasonRequired: "(จำเป็น)",
    reasonCount: "10 ถึง 500 ตัวอักษร · ตอนนี้ {n}",
    reasonShort: "ใส่เหตุผลอย่างน้อย 10 ตัวอักษร (ตอนนี้ {n})",
    reasonLong: "เหตุผลยาวได้ไม่เกิน 500 ตัวอักษร (ตอนนี้ {n})",
    send: "ส่งคำขอ",
    sending: "กำลังส่ง…",
    cancel: "ยกเลิก",
    close: "ปิด",
    rateLimited: "ขอใหม่ได้เวลา {time}",
    rateLimitedNoTime: "ขอรหัสฉุกเฉินบนงานนี้บ่อยเกินไป ลองใหม่ภายหลัง",
    noApprover: "ไม่มีผู้รับรหัสฉุกเฉินสำหรับโปรเจกต์นี้ ขอให้ System Admin ตั้งผู้อนุมัติสำรองใน Settings → Security",
    notConfigured: "เซิร์ฟเวอร์นี้ยังไม่ได้ตั้งค่ารหัสฉุกเฉิน",
    alreadyActive: "คุณมีสิทธิ์ฉุกเฉินบนงานนี้อยู่แล้ว",
    staffOnly: "รหัสฉุกเฉินสำหรับพนักงาน TFG เท่านั้น",
    testMode: "ใช้รหัสฉุกเฉินในโหมดทดสอบไม่ได้ ปิดโหมดทดสอบก่อน",
    codeTitle: "ใส่รหัสฉุกเฉินของ {key}",
    sentProjectDirector: "ส่งถึง {names} Chief Pilot ของ {project} {channel}",
    sentFallback: "ส่งถึงผู้อนุมัติสำรอง {names} {channel}",
    sentSelf: "ออกรหัสให้ตัวเอง: คุณเป็นผู้อนุมัติคนสุดท้ายของโปรเจกต์นี้ จะถูกรายงาน",
    channelBoth: "ทาง Telegram และใน Giraffe",
    channelInApp: "ใน Giraffe เท่านั้น (ยังไม่เชื่อม Telegram)",
    callThem: "โทรหรือถามรหัสต่อหน้า",
    codeLabel: "รหัส 6 หลักจาก Chief Pilot",
    codeLabelOther: "รหัส 6 หลักจากผู้อนุมัติ",
    codeFormat: "ใส่รหัส 6 หลัก",
    expiresIn: "หมดอายุใน {time}",
    expiresSoon: "รหัสจะหมดอายุใน 1 นาที",
    attemptsLeft: "รหัสไม่ถูกต้อง เหลือ {n} ครั้ง",
    attemptLeft: "รหัสไม่ถูกต้อง เหลือ 1 ครั้ง",
    attemptsLeftShort: "เหลือ {n} ครั้ง",
    attemptLeftShort: "เหลือ 1 ครั้ง",
    unlock: "ปลดล็อก",
    unlocking: "กำลังตรวจ…",
    cancelRequest: "ยกเลิกคำขอ",
    cancelling: "กำลังยกเลิก…",
    newRequest: "ขอรหัสใหม่",
    newRequestNote: "คำขอใหม่จะแทนคำขอนี้ รหัสเดิมจะใช้ไม่ได้",
    expired: "รหัสนี้หมดอายุแล้ว ขอใหม่หากยังจำเป็น",
    locked: "ถูกล็อกหลังใส่รหัสผิด 5 ครั้ง ผู้อนุมัติได้รับแจ้งแล้ว ขอรหัสใหม่หากยังจำเป็น",
    notPending: "คำขอนี้ปิดแล้ว ขอรหัสใหม่หากยังจำเป็น",
    grantedAnnounce: "สิทธิ์ฉุกเฉินเปิดใช้บน {key}",
    cancelledAnnounce: "ยกเลิกคำขอรหัสฉุกเฉินแล้ว",
    bannerTitle: "สิทธิ์ฉุกเฉินเปิดใช้อยู่",
    bannerBody: "{holder} ย้ายงานนี้ได้ทุกขั้นจนกว่าจะเสร็จ",
    bannerBodyYou: "คุณย้ายงานนี้ได้ทุกขั้นจนกว่าจะเสร็จ",
    bannerFrom: "รหัสจาก {approver} {ago}",
    selfIssued: "ออกให้ตัวเอง",
    viewMoves: "ดูการย้าย",
    endTitle: "ยุติสิทธิ์ฉุกเฉินบน {key}?",
    endBodyHolder: "คุณจะข้ามกฎบนงานนี้ไม่ได้อีก สิทธิ์ใหม่ต้องใช้รหัสใหม่",
    endBodyOther: "{holder} จะข้ามกฎบนงานนี้ไม่ได้อีก ผู้ถือและผู้อนุมัติจะได้รับแจ้ง",
    endNote: "หมายเหตุ",
    endNoteOptional: "(ไม่บังคับ)",
    endNoteRequired: "(จำเป็น)",
    endNoteShort: "บอกเหตุผลอย่างน้อย 10 ตัวอักษร (ตอนนี้ {n})",
    endConfirm: "ยุติสิทธิ์",
    ending: "กำลังยุติ…",
    endedAnnounce: "ยุติสิทธิ์ฉุกเฉินบน {key} แล้ว",
    notActive: "สิทธิ์นี้ยุติไปแล้ว",
    moveChip: "ฉุกเฉิน",
    moveSkips: "การย้ายนี้ข้าม:",
    moveSkipsNone: "ย้ายได้ด้วยสิทธิ์ฉุกเฉินเท่านั้น",
    moveConfirm: "{label} (ฉุกเฉิน)",
    moveOptional: "ช่องต่าง ๆ ไม่บังคับภายใต้สิทธิ์ฉุกเฉิน ยกเว้นช่องที่บันทึกต้องใช้",
    pageTitle: "รหัสฉุกเฉิน",
    pageLoading: "กำลังโหลด…",
    pageNotAvailable: "คุณไม่มีสิทธิ์ดูคำขอรหัสฉุกเฉินนี้",
    pageCard: "งาน",
    pageRequester: "ผู้ขอ",
    pageReason: "เหตุผล",
    pageStatus: "สถานะ",
    pageExpiresIn: "หมดอายุใน {time}",
    showCode: "แสดงรหัส",
    showingCode: "กำลังโหลดรหัส…",
    codeAria: "รหัสฉุกเฉิน {digits}",
    copy: "คัดลอก",
    copied: "คัดลอกแล้ว",
    copyFailed: "คัดลอกไม่ได้ อ่านตัวเลขให้ผู้ขอฟังแทน",
    giveOnly: "ให้รหัสนี้กับ {requester} เท่านั้น ทางโทรศัพท์หรือต่อหน้า",
    revealGone: "แสดงรหัสไม่ได้แล้ว: คำขอถูกใช้ หมดอายุ หรือถูกล็อก",
    outcomeConsumed: "{requester} ใช้เมื่อ {time}",
    outcomeExpired: "หมดอายุ",
    outcomeLocked: "ถูกล็อกหลังใส่รหัสผิด 5 ครั้ง",
    outcomeSuperseded: "ถูกแทนด้วยคำขอใหม่จาก {requester}",
    outcomeCancelled: "{requester} ยกเลิกแล้ว",
    outcomePending: "รอ {requester} ใส่รหัส",
    bellRequested: "ขอรหัสฉุกเฉิน",
    bellExpiresIn: "หมดอายุใน {n} นาที",
    bellExpired: "หมดอายุแล้ว",
    bellGranted: "เปิดสิทธิ์ฉุกเฉิน",
    bellMoved: "ย้ายงานนี้ด้วยสิทธิ์ฉุกเฉิน",
    bellLocked: "รหัสฉุกเฉินถูกล็อกหลังใส่ผิด 5 ครั้ง",
    bellEnded: "สิทธิ์ฉุกเฉินยุติแล้ว",
    bellStillActive: "สิทธิ์ฉุกเฉินยังเปิดอยู่",
    attention: "รหัสฉุกเฉินรออยู่ ({n})",
    fallbackHeading: "รหัสฉุกเฉิน",
    fallbackLabel: "ผู้อนุมัติสำรองกรณีฉุกเฉิน",
    fallbackHelp: "ได้รับรหัสฉุกเฉินของโปรเจกต์ที่ไม่มี Chief Pilot ว่าง = System Admin ทุกคน",
    fallbackEveryone: "System Admin ทุกคน (ว่าง)",
    fallbackReason: "เหตุผล",
    fallbackSave: "บันทึกผู้อนุมัติสำรอง",
    fallbackSaving: "กำลังบันทึก…",
    fallbackSaved: "บันทึกผู้อนุมัติสำรองแล้ว",
    fallbackNoAdmins: "ไม่มี System Admin ที่เลือกได้",
    fallbackStale: "มีคนแก้การตั้งค่านี้หลังคุณเปิดหน้า โหลดใหม่เพื่อดู",
    historyVia: "สิทธิ์ฉุกเฉิน",
    historyField: "สิ่งที่สิทธิ์ฉุกเฉินข้าม",
    youSuffix: "(คุณ)",
    reportTitle: "สิทธิ์ฉุกเฉิน",
    reportSub: "สิทธิ์ฉุกเฉินทั้งหมด ผู้ขอ ผู้ให้รหัส และทุกการย้ายที่ทำด้วยสิทธิ์นั้น Chief Pilot เห็นโปรเจกต์ของตน System Admin เห็นทุกโปรเจกต์",
    reportNotAllowed: "รายงานสิทธิ์ฉุกเฉินสำหรับ Chief Pilot และ System Admin เท่านั้น",
    reportFlagOff: "ยังไม่ได้เปิดใช้รหัสฉุกเฉิน",
    reportTabs: "ส่วนของรายงาน",
    reportTabBypasses: "สิทธิ์ที่ใช้",
    reportTabRequests: "คำขอที่ไม่ได้ใช้",
    reportFilters: "กรองสิทธิ์ฉุกเฉิน",
    reportProject: "โปรเจกต์",
    reportAllProjects: "ทุกโปรเจกต์ของฉัน",
    reportFrom: "ให้สิทธิ์ตั้งแต่",
    reportTo: "ถึง",
    reportRequestedFrom: "ขอตั้งแต่",
    reportShow: "แสดง",
    reportActive: "เปิดอยู่",
    reportEnded: "ยุติแล้ว",
    reportAll: "ทั้งหมด",
    reportAllRequests: "ทั้งหมดที่ไม่ได้ใช้",
    reportRequester: "ผู้ขอ",
    reportAnyone: "ทุกคน",
    reportClear: "ล้างตัวกรอง",
    reportColCard: "งาน",
    reportColProject: "โปรเจกต์",
    reportColRequester: "ผู้ขอ",
    reportColCodeFrom: "รหัสจาก",
    reportColReason: "เหตุผล",
    reportColGranted: "ให้สิทธิ์",
    reportColEnded: "ยุติ",
    reportColEndedBecause: "สาเหตุที่ยุติ",
    reportColDuration: "ระยะเวลา",
    reportColMoves: "การย้าย",
    reportColApprovers: "ส่งถึง",
    reportColRequested: "ขอเมื่อ",
    reportColStatus: "ผล",
    reportColAttempts: "รหัสผิด",
    reportColClosed: "ปิดเมื่อ",
    reportActiveFor: "เปิดมา {time}",
    reportStillActive: "ยังเปิดอยู่",
    reportEndedDone: "งานเสร็จ",
    reportEndedHolder: "ผู้ถือยุติเอง",
    reportEndedRevoked: "ถูกยุติก่อนกำหนด",
    reportEndedIneligible: "ผู้ถือไม่มีสิทธิ์แล้ว",
    reportEndedBy: "โดย {name}",
    reportReqPending: "รอใส่รหัส",
    reportReqExpired: "หมดอายุ",
    reportReqLocked: "ถูกล็อกหลังใส่รหัสผิด 5 ครั้ง",
    reportReqSuperseded: "ถูกแทนด้วยคำขอใหม่",
    reportReqCancelled: "ยกเลิก",
    reportReqUsed: "ใช้แล้ว",
    reportBasisPd: "Chief Pilot",
    reportBasisFallback: "ผู้อนุมัติสำรอง",
    reportShowMoves: "แสดงการย้าย",
    reportHideMoves: "ซ่อนการย้าย",
    reportMovesOf: "การย้ายภายใต้สิทธิ์ {id} บน {key}",
    reportNoMoves: "ยังไม่มีการย้าย",
    reportNothingSkipped: "ไม่ได้ข้ามอะไร",
    reportSkips: "ข้าม: {list}",
    reportLoading: "กำลังโหลดสิทธิ์ฉุกเฉิน…",
    reportLoadFailed: "โหลดสิทธิ์ฉุกเฉินไม่ได้",
    reportEmpty: "ไม่มีสิทธิ์ฉุกเฉินที่ตรงกับตัวกรอง",
    reportEmptyRequests: "ไม่มีคำขอที่ไม่ได้ใช้ที่ตรงกับตัวกรอง",
    reportCountBypass: "สิทธิ์ฉุกเฉิน 1 รายการ",
    reportCountBypasses: "สิทธิ์ฉุกเฉิน {n} รายการ",
    reportCountRequest: "คำขอ 1 รายการ",
    reportCountRequests: "คำขอ {n} รายการ",
    reportMoreAvailable: "{count} · ยังมีอีก",
    reportAutomated: "เปิดรายงานสิทธิ์ฉุกเฉินผ่านผู้ช่วย AI หรือเบราว์เซอร์อัตโนมัติไม่ได้ เปิดในเบราว์เซอร์ของคุณเอง",
    reportLoadMore: "โหลดเพิ่ม",
    reportLoadingMore: "กำลังโหลด…",
    reportExport: "ส่งออก CSV",
    reportExporting: "กำลังส่งออก…",
    reportExported: "ดาวน์โหลด CSV แล้ว: {n} แถว",
    reportExportCapped: "ส่งออกเฉพาะ {n} รายการแรก กรองให้แคบลงเพื่อส่งออกส่วนที่เหลือ",
    reportExportFailed: "ส่งออกไม่สำเร็จ ไม่มีไฟล์ถูกดาวน์โหลด",
    reportDateOrder: "วันที่เริ่มอยู่หลังวันที่สิ้นสุด",
    reportLockedNote: "ใส่รหัสผิด 5 ครั้ง: ตรวจสอบกับผู้ขอว่าเป็นเขาจริง",
    requestWhoLoading: "กำลังตรวจสอบผู้อนุมัติ…",
    requestWhoNamed: "รหัสจะส่งถึง {list} โทรหาผู้รับหลังจากส่งคำขอ",
    requestWhoPerson: "{name} ({role}) {channel}",
    requestWhoAnd: " และ ",
    whoChannelBoth: "ทาง Telegram และกระดิ่งแจ้งเตือน",
    whoChannelBell: "ทางกระดิ่งแจ้งเตือนเท่านั้น (ยังไม่ได้เชื่อม Telegram)",
    requestWhoSelf: "คุณเป็นผู้อนุมัติของโครงการนี้: รหัสจะส่งถึงคุณเอง",
    boardChip: "ฉุกเฉิน",
    reportAnyDate: "{label} (ทุกวันที่)",
    reportExportBypasses: "ส่งออกสิทธิ์ฉุกเฉิน (CSV)",
    reportExportRequests: "ส่งออกคำขอ (CSV)",
    reportExportNothing: "ไม่มีข้อมูลให้ส่งออกตามตัวกรองนี้",
    reportExportFixDates: "แก้ไขวันที่ก่อนส่งออก",
  },
};

/** Emergency chrome is English-only regardless of the caller's locale (2026-10-04): see `chromeLocale`. */
export function emergencyText(key: EmergencyTextKey, values: Record<string, string | number> = {}, locale?: Locale): string {
  return EMERGENCY_TEXT[chromeLocale(locale)][key].replace(/\{(\w+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match));
}
